import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Alert, Box, Button, Chip, CircularProgress, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import DataTable from '../shared/DataTable';
import { useUserStore } from '../../store';
import { codeFont, numFont, plateFont } from '../../utils/fonts';
import CabasEstimatePreview, { formatCabasDate } from './CabasEstimatePreview';

/**
 * Pick a CABAS estimate and import it.
 *
 * Without `maintenance` the import creates a new work order; with it, the estimate is
 * imported into that work order. Lists the estimates CABAS marks ready to be invoiced
 * (last 31 days) and searches by registration or work order number; on a work order it
 * starts with that car's estimates, ready or not. The import is always re-fetched from
 * CAB on the server; only the identity and the previewed version are sent.
 *
 * onImported(result)      result = { mode, changed, maintenance: { id, work_number } }
 * onOpenMaintenance(id)   open another work order that already holds the estimate
 */
export default function CabasEstimatePicker({ maintenance = null, onImported, onOpenMaintenance, onCancel }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const orgId = currentOrganization?.organization_id;

  const [step, setStep] = useState('list'); // list | preview
  const [query, setQuery] = useState(maintenance?.vehicle?.registration_number || '');
  const [search, setSearch] = useState(null); // ready | vehicle | work_order
  const [records, setRecords] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');

  const [identity, setIdentity] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [insurerCustomer, setInsurerCustomer] = useState(null);
  const [marginPrices, setMarginPrices] = useState({});

  const loadList = useCallback(
    async (plate = '') => {
      if (!orgId) return;
      setListLoading(true);
      setListError('');
      try {
        const params = new URLSearchParams({ organization: orgId });
        if (plate.trim()) params.set('q', plate.trim());
        const resp = await axios.get(`/api/cabas/estimates/?${params.toString()}`);
        setRecords(resp.data.records || []);
        setSearch(resp.data.search);
      } catch (err) {
        setRecords([]);
        setListError(err.response?.data?.error || t('cabas.picker.loadError'));
      } finally {
        setListLoading(false);
      }
    },
    [orgId, t]
  );

  // On a work order, start with that car's estimates: the estimate is often fetched
  // before CABAS marks it ready to be invoiced.
  const initialPlate = maintenance?.vehicle?.registration_number || '';
  useEffect(() => {
    loadList(initialPlate);
  }, [loadList, initialPlate]);

  const loadPreview = useCallback(
    async (estimateIdentity) => {
      setIdentity(estimateIdentity);
      setStep('preview');
      setPreview(null);
      setPreviewError('');
      setImportError(null);
      setInsurerCustomer(null);
      setMarginPrices({});
      setPreviewLoading(true);
      try {
        const params = new URLSearchParams({ organization: orgId });
        if (maintenance?.id) params.set('maintenance', maintenance.id);
        const resp = await axios.get(`/api/cabas/estimates/${encodeURIComponent(estimateIdentity)}/preview/?${params}`);
        setPreview(resp.data);
        // A price entered on an earlier import of the estimate is offered again.
        setMarginPrices(
          Object.fromEntries(
            (resp.data.margin_parts || [])
              .filter((part) => part.purchase_price != null)
              .map((part) => [part.key, String(part.purchase_price)])
          )
        );
      } catch (err) {
        setPreviewError(err.response?.data?.error || t('cabas.picker.previewError'));
      } finally {
        setPreviewLoading(false);
      }
    },
    [orgId, maintenance?.id, t]
  );

  const handleImport = async () => {
    setImporting(true);
    setImportError(null);
    try {
      const body = {
        maintenance: maintenance?.id || null,
        expected_version: preview.version,
        insurer_customer: insurerCustomer?.id || null,
      };
      if (preview.margin_parts?.length) body.margin_purchase_prices = marginPrices;
      const resp = await axios.post(
        `/api/cabas/estimates/${encodeURIComponent(identity)}/import/?organization=${orgId}`,
        body
      );
      await onImported?.(resp.data);
    } catch (err) {
      const data = err.response?.data || {};
      if (data.error_code === 'version_changed') {
        // CABAS has a newer version than the one shown: show that one instead.
        await loadPreview(identity);
      }
      setImportError({
        message: data.error || t('cabas.picker.importError'),
        maintenance: data.error_code === 'linked_elsewhere' ? data.maintenance : null,
      });
    } finally {
      setImporting(false);
    }
  };

  const backToList = () => {
    setStep('list');
    setPreview(null);
    setPreviewError('');
    setImportError(null);
  };

  const openLinked = (target) => target?.id && onOpenMaintenance?.(target.id);

  if (step === 'preview') {
    const marginPricesMissing = (preview?.margin_parts || []).some((part) => !String(marginPrices[part.key] ?? '').trim());
    const canOpenLinked =
      preview &&
      ['up_to_date', 'linked_elsewhere'].includes(preview.mode) &&
      preview.maintenance?.id &&
      preview.maintenance.id !== maintenance?.id;
    return (
      <Box data-testid="cabas-picker-preview">
        {previewLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {previewError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {previewError}
          </Alert>
        )}
        {preview && (
          <CabasEstimatePreview
            preview={preview}
            insurerCustomer={insurerCustomer}
            onInsurerCustomerChange={setInsurerCustomer}
            marginPrices={marginPrices}
            onMarginPriceChange={(key, value) => setMarginPrices((prices) => ({ ...prices, [key]: value }))}
          />
        )}
        {importError && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="cabas-import-error">
            {importError.message}
          </Alert>
        )}
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            startIcon={<ArrowBackOutlined />}
            onClick={backToList}
            disabled={importing}
            sx={{ mr: 'auto' }}
            data-testid="cabas-back"
          >
            {t('cabas.picker.back')}
          </Button>
          {onCancel && (
            <Button variant="outlined" color="inherit" onClick={onCancel} disabled={importing}>
              {t('Cancel')}
            </Button>
          )}
          {(canOpenLinked || importError?.maintenance?.id) && (
            <Button
              variant="outlined"
              onClick={() => openLinked(importError?.maintenance?.id ? importError.maintenance : preview.maintenance)}
              data-testid="cabas-open-linked"
            >
              {t('cabas.picker.openWorkOrder')}
            </Button>
          )}
          {preview?.can_import && (
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={importing || marginPricesMissing}
              startIcon={importing ? <CircularProgress size={18} color="inherit" /> : null}
              data-testid="cabas-import"
            >
              {preview.mode === 'create'
                ? t('cabas.picker.createWorkOrder')
                : preview.mode === 'update'
                  ? t('cabas.picker.updateWorkOrder')
                  : t('cabas.picker.importToWorkOrder')}
            </Button>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Box data-testid="cabas-picker-list">
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          loadList(query);
        }}
        sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}
      >
        <TextField
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('cabas.picker.searchPlaceholder')}
          sx={{ flex: '1 1 200px' }}
          inputProps={{ 'data-testid': 'cabas-search', style: plateFont }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button type="submit" variant="outlined" disabled={listLoading || !query.trim()} data-testid="cabas-search-submit">
          {t('cabas.picker.search')}
        </Button>
        {search !== 'ready' && (
          <Button
            onClick={() => {
              setQuery('');
              loadList('');
            }}
            disabled={listLoading}
          >
            {t('cabas.picker.showReady')}
          </Button>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {search === 'vehicle'
          ? t('cabas.picker.searchResults')
          : search === 'work_order'
            ? t('cabas.picker.workOrderResults')
            : t('cabas.picker.readyHelp')}
      </Typography>

      {listError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="cabas-list-error">
          {listError}
        </Alert>
      )}

      <DataTable
        columns={[
          {
            key: 'plate',
            label: t('cabas.picker.registration'),
            width: '1%',
            noWrap: true,
            render: (row) => (
              <Box component="span" sx={{ ...plateFont, fontWeight: 700 }}>
                {row.vehicle_identification || '-'}
              </Box>
            ),
          },
          {
            key: 'claim',
            label: t('cabas.picker.claimNumber'),
            maxWidth: 200,
            render: (row) => <Box component="span" sx={codeFont}>{row.claim_number}</Box>,
          },
          {
            key: 'workOrderNumber',
            label: t('cabas.picker.workOrderNumber'),
            noWrap: true,
            hideBelow: 'md',
            render: (row) => <Box component="span" sx={numFont}>{row.work_order_number}</Box>,
          },
          {
            key: 'date',
            label: t('cabas.picker.damageDate'),
            noWrap: true,
            hideBelow: 'sm',
            render: (row) => <Box component="span" sx={numFont}>{formatCabasDate(row.damage_date)}</Box>,
          },
          {
            key: 'linked',
            label: '',
            width: '1%',
            noWrap: true,
            render: (row) =>
              row.linked ? (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={
                    row.linked.work_number
                      ? t('cabas.picker.onWorkOrder', { number: row.linked.work_number })
                      : t('cabas.picker.imported')
                  }
                />
              ) : null,
          },
        ]}
        data={records.map((record) => ({ ...record, id: record.identity }))}
        alwaysVisible
        horizontalScroll
        loading={listLoading}
        onRowClick={(row) => loadPreview(row.identity)}
        emptyTitle={search === 'ready' ? t('cabas.picker.noReady') : t('cabas.picker.noResults')}
      />
      {onCancel && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="outlined" color="inherit" onClick={onCancel}>
            {t('Cancel')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
